
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import Button from 'components/Button'
import Dialog from 'components/Dialog'
import TableSolicitud from './components/TableSolicitud'
import ViewSolicitud from './components/ViewSolicitud'
import FormDeny from './components/FormDeny'
import FormPresupuesto from './components/FormPresupuesto'

import SolicitudService from 'services/solicitud.service'
import { setHttpMessage } from 'store/messageSlice'
import { buildFilters, formatDate } from 'utils/utils'
import TitlePage from 'components/TitlePage'
import useFilters from 'hooks/useFilters'
import { initialPagination } from 'data/const'

/** @module Pages/Solicitud/SolicitudPresupuestoIndex */

const currentYear = formatDate(new Date(), 'Y')

/**
 * Página, Lista de solicitudes de Presupuestos.
 * @returns {JSX.Element} Retorna el componente SolicitudPresupuestoIndex.
 */
function SolicitudPresupuestoIndex() {
    const dispatch = useDispatch()
    const [solicitudes, setSolicitudes] = useState([])
    const [pagination, setPagination] = useState(initialPagination)
    const [selected, setSelected] = useState(null)
    const {filters, reloadFilters, ...filterHandlers} = useFilters()
    const [show, setShow] = useState({info: false, deny: false, budget: false})
    const [pendientes, setPendientes] = useState(false)
    const denyRef = useRef()
    const tableRef = useRef()
    const formRef = useRef()

    useEffect(() => {
        const loadSolicitudes = async () => {
            const {filter, ..._filters} = filters
            const _filter = {
                ...filter,
                ...buildFilters(
                    {
                        ...(pendientes&&{presupuestado: false}),
                        ...(!filter?.fecha_creacion&&{
                            fecha_creacion: [
                                `${currentYear}-01-01 00:00:00`,
                                `${currentYear}-12-31 23:59:59`
                            ]
                        })
                    }, 
                    {in: ['estado'], bool: ['presupuestado'], between: ['fecha_creacion']}
                ),
            }
            const response = await SolicitudService.indexSolicitudPost({filter: _filter}, {..._filters, tipo: 'P'})
            if (response.status === 200) {
                setSolicitudes(response.data.data)
                setPagination(response.data.pagination)
            }
        }
        loadSolicitudes()
    }, [filters, pendientes])

    const openDialog = (name) => setShow({...show, [name]: true})
    const closeInfoDialog = () => setShow({...show, info: false})
    const closeDenyDialog = () => setShow({...show, deny: false})
    const closeBudgetDialog = () => setShow({...show, budget: false})

    const handleOpenDialog = (action, rowData) => {
        setSelected(rowData)
        if (action === 'deny') {
            openDialog('deny')
        } else if (action === 'view') {
            openDialog('info')
        } else if (action === 'budget') {
            openDialog('budget')
        }
    }

    const handleDeny = async (values) => {
        if (selected) {
            const response = await SolicitudService.denegar(values, selected.id)
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeDenyDialog()
            }
        }
    }
    const handleBudget = async (values) => {
        if (selected) {
            const response = await SolicitudService.addPresupuesto(values, { id: selected.id })
            dispatch(setHttpMessage({status: response.status, title: response.data.message}))
            if (response.status === 200) {
                reloadFilters()
                closeBudgetDialog()
            }
        }
    }
    const cleanSelected = () => setSelected(null)

    const footerDenyDialog = <>
        <Button startIcon='pi pi-times' variant='outline-blue' onClick={closeDenyDialog}>Cerrar</Button>
        <Button startIcon='pi pi-save' onClick={() => denyRef?.current?.handleSubmit()}>Guardar</Button>
    </>
    const footerBudgetDialog = <>
        <Button startIcon='pi pi-times' variant='outline-blue' onClick={closeBudgetDialog}>Cerrar</Button>
        <Button startIcon='pi pi-save' onClick={() => formRef?.current?.handleSubmit()}>Guardar</Button>
    </>
    const toolbarOptions = <div className='btn-group'>
        <Button variant='tab-red' active={!pendientes} onClick={() => setPendientes(false)}>Todos</Button>
        <Button variant='tab-red' active={pendientes} onClick={() => setPendientes(true)}>Pendientes</Button>
    </div>

    return <>
        <TitlePage title='Lista de Solicitudes' />
        <div className='content'>
            <div>
                <TableSolicitud 
                    tableRef={tableRef}
                    solicitudes={solicitudes}
                    pagination={pagination}
                    filterHandlers={filterHandlers}
                    handleOpenDialog={handleOpenDialog}
                    toolbarOptions={toolbarOptions}
                    dataTableProps={{
                        rowClassName: (rowData) => (rowData.presupuestado?'bg-green-50':'bg-orange-50'),
                        tableClassName: '',
                        tableStyle: {borderColor: '#c7cace'}
                    }}
                />

                <Dialog
                    show={show.info}
                    header='Información'
                    footer={<Button startIcon='pi pi-times' onClick={closeInfoDialog}>Cerrar</Button>}
                    onHide={closeInfoDialog}
                    style={{width: 'auto', maxWidth: '60rem'}}
                    onExited={cleanSelected}
                    scrollable
                >
                    <ViewSolicitud id={selected?.id} />
                </Dialog>
                <Dialog
                    show={show.deny}
                    header='Registrar observaciones'
                    footer={footerDenyDialog}
                    keyboard={false}
                    style={{width: '30rem'}}
                    onHide={closeDenyDialog}
                    onExited={cleanSelected}
                >
                    <FormDeny formRef={denyRef} onSubmit={handleDeny} selected={selected} />
                </Dialog>
                <Dialog
                    show={show.budget}
                    header='Asignar Certificación Presupuestaria'
                    footer={footerBudgetDialog}
                    keyboard={false}
                    style={{width: '50rem'}}
                    onHide={closeBudgetDialog}
                    onExited={cleanSelected}
                >
                    <FormPresupuesto formRef={formRef} onSubmit={handleBudget} solicitud={selected} />
                </Dialog>
                
            </div>
        </div>
    </>
}
export default SolicitudPresupuestoIndex